import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { Carousel, useCarousel } from '@shared/carousel';

import { useIsomorphicBreakpoints } from '@utils/hooks/use_breakpoints';

import arrowRight from '@images/home/icon-arrow-right.svg';
import starAccent from '@images/home/icon-star-accent.svg';
import starFull from '@images/home/icon-star-full.svg';
import trustPilotLogo from '@images/home/logo-trustpilot.svg';
import mountainsBg from '@images/home/mountains-bg.png';

import { HOME_TESTIMONIALS } from './data';
import { EntranceHeadline } from './entrance_headline';
import {
  BodyMd,
  BodySm,
  HeadingH2,
  HeadingH3,
  HomeDesktopOnly,
  HomeMobileOnly,
  LabelSm,
  Section,
  SectionContainer,
} from './styles';
import { MEDIA, TOKENS } from './tokens';
import {
  MOUNTAINS_PARALLAX_INTENSITY,
  useElementParallax,
} from './use_parallax';

const TestimonialsSection = styled(Section)`
  position: relative;
  overflow: hidden;
  /* Figma mobile: 40px horizontal inset, 64px top padding */
  padding-left: ${TOKENS.space[40]};
  padding-right: ${TOKENS.space[40]};
  padding-top: ${TOKENS.space[64]};
  padding-bottom: ${TOKENS.space[64]};

  ${MEDIA.lg} {
    padding-left: ${TOKENS.space[48]};
    padding-right: ${TOKENS.space[48]};
    padding-top: ${TOKENS.space[64]};
    padding-bottom: ${TOKENS.space[64]};
  }
`;

const MountainsBackground = styled.img<{ $parallaxY: number }>`
  position: absolute;
  left: 50%;
  bottom: -18%;
  width: min(640px, 140vw);
  height: auto;
  opacity: 0.15;
  pointer-events: none;
  user-select: none;
  will-change: transform;
  transform: translate3d(-50%, ${({ $parallaxY }) => `${$parallaxY}px`}, 0);

  @media (prefers-reduced-motion: reduce) {
    transform: translate3d(-50%, 0, 0);
  }

  ${MEDIA.lg} {
    left: auto;
    right: -6%;
    bottom: -36%;
    width: min(960px, 62vw);
    transform: translate3d(0, ${({ $parallaxY }) => `${$parallaxY}px`}, 0);

    @media (prefers-reduced-motion: reduce) {
      transform: none;
    }
  }
`;

const Layout = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[40]};
  min-height: 0;
  width: 100%;
  min-width: 0;

  ${MEDIA.lg} {
    flex-direction: row;
    align-items: stretch;
    justify-content: space-between;
    gap: ${TOKENS.space[80]};
  }
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[24]};
  width: 100%;
  flex-shrink: 0;

  ${MEDIA.lg} {
    width: 360px;
    max-width: 360px;
    gap: ${TOKENS.space[32]};
    justify-content: space-between;
    min-height: 100%;
    padding: ${TOKENS.space[32]} 0;
  }
`;

const IntroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[16]};

  ${MEDIA.lg} {
    gap: ${TOKENS.space[16]};
  }
`;

const TrustBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${TOKENS.space[12]};
  width: fit-content;
  align-self: flex-start;
  border-radius: ${TOKENS.radius.pill};
  border: 1px solid ${TOKENS.color.neutral0_20};
  background: ${TOKENS.color.neutral0_5};
  padding: ${TOKENS.space[12]} ${TOKENS.space[16]};
  color: ${TOKENS.color.iconFillLight};
  text-decoration: none;
  transition: background 0.15s ease;
  margin-top: 0;

  &:hover {
    background: ${TOKENS.color.neutral0_10};
    color: ${TOKENS.color.iconFillLight};
  }

  &:active {
    background: ${TOKENS.color.neutral0_12};
  }

  img[alt='Trustpilot'] {
    display: block;
    width: 70px;
    height: 17px;
    flex-shrink: 0;
  }
`;

const StarsRow = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  gap: ${TOKENS.space[4]};

  img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const CARD_SIZE_PX = 280;
const CARD_GAP_PX = 16;
/** Slide pitch = card + trailing gap; three visible pitches wide. */
const DESKTOP_SLIDE_PITCH_PX = CARD_SIZE_PX + CARD_GAP_PX;
const DESKTOP_CARDS_RAIL_PX = DESKTOP_SLIDE_PITCH_PX * 3;

const CarouselWrap = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  width: 100%;

  ${MEDIA.lg} {
    flex: 0 1 auto;
    width: min(100%, ${DESKTOP_CARDS_RAIL_PX}px);
    max-width: ${DESKTOP_CARDS_RAIL_PX}px;
  }
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${TOKENS.space[12]};
  margin-top: ${TOKENS.space[24]};
  order: 2;
  width: 100%;

  ${MEDIA.lg} {
    order: 0;
    justify-content: flex-end;
    /* Match slide trailing gap so arrows share the card’s right edge. */
    padding-right: ${CARD_GAP_PX}px;
    margin-top: 0;
    margin-bottom: ${TOKENS.space[24]};
    box-sizing: border-box;
  }
`;

const CarouselTrack = styled.div`
  order: 1;
  width: 100%;
`;

const CarouselButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${TOKENS.radius.pill};
  border: 1px solid ${TOKENS.color.neutral0_20};
  background: ${TOKENS.color.neutral0_5};
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: ${TOKENS.color.neutral0_10};
    border-color: ${TOKENS.color.neutral0_30};
  }

  &:active:not(:disabled) {
    background: ${TOKENS.color.neutral0_12};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const CarouselOuter = styled.div`
  width: 100%;
  overflow: hidden;
`;

const TestimonialCarousel = styled(Carousel)`
  width: 100%;

  & > div {
    align-items: stretch;
  }
`;

const CarouselSlide = styled.div`
  display: flex;
  height: 100%;
  align-self: stretch;
  flex-shrink: 0;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 0;
  box-sizing: border-box;

  ${MEDIA.lg} {
    padding-right: ${CARD_GAP_PX}px;
  }
`;

const TestimonialCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${TOKENS.space[16]};
  width: 100%;
  min-width: 0;
  min-height: 220px;
  border-radius: ${TOKENS.radius.sm};
  border: none;
  background: ${TOKENS.color.neutral0_10};
  backdrop-filter: blur(50px);
  -webkit-backdrop-filter: blur(50px);
  color: ${TOKENS.color.inverse};
  padding: ${TOKENS.space[16]};
  box-sizing: border-box;

  ${MEDIA.lg} {
    flex: 1 0 0;
    gap: ${TOKENS.space[24]};
    width: 100%;
    max-width: none;
    height: ${CARD_SIZE_PX}px;
    min-height: ${CARD_SIZE_PX}px;
    background: ${TOKENS.color.neutral0_5};
  }
`;

const CardStars = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  gap: ${TOKENS.space[4]};

  img {
    width: 16px;
    height: 16px;
  }
`;

const IntroBody = styled(BodyMd)`
  font-family: ${TOKENS.font.sans};
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${TOKENS.color.onDarkMuted};

  ${MEDIA.lg} {
    font-size: ${TOKENS.type.bodyMdSize};
    line-height: ${TOKENS.type.bodyMdLineHeight};
    font-weight: ${TOKENS.type.bodyMdWeight};
    color: ${TOKENS.color.inverse};
  }
`;

const CardQuote = styled(HeadingH3)`
  min-width: 0;
  width: 100%;
  font-family: ${TOKENS.font.sans};
  font-size: 16px;
  font-style: normal;
  line-height: 22px;
  font-weight: 500;
  color: ${TOKENS.color.inverse};

  ${MEDIA.lg} {
    font-size: ${TOKENS.type.headingH3Size};
    line-height: ${TOKENS.type.headingH3LineHeight};
    font-weight: ${TOKENS.type.headingH3Weight};
  }
`;

const CardCopy = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: ${TOKENS.space[12]};
  min-height: 0;
  min-width: 0;
  width: 100%;
`;

const CardBody = styled(BodySm)`
  display: block;
  min-width: 0;
  width: 100%;
  font-family: ${TOKENS.font.sans};
  font-size: 13px;
  font-style: normal;
  line-height: 18px;
  font-weight: 500;
  color: ${TOKENS.color.inverse};

  ${MEDIA.lg} {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: ${TOKENS.color.inverse};
  }
`;

/** Figma Label / name under the review — smaller and muted vs body copy. */
const CardAuthor = styled(LabelSm)`
  display: block;
  flex-shrink: 0;
  margin-top: auto;
  width: 100%;
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${TOKENS.color.onDarkMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StarIcons = ({
  fullCount = 4,
  variant = 'trust',
}: {
  fullCount?: number;
  variant?: 'trust' | 'card';
}) => {
  const fullStar = variant === 'card' ? starAccent : starFull;

  return (
    <>
      {Array.from({ length: fullCount }).map((_, i) => (
        <img key={`full-${i}`} src={fullStar} alt="" />
      ))}
      {fullCount < 5 && (
        <img
          src={fullStar}
          alt=""
          style={{ opacity: variant === 'trust' ? 0.35 : 1 }}
        />
      )}
    </>
  );
};

const TrustStars = styled(StarsRow)`
  img {
    filter: none;
  }
`;

const SLIDE_COUNT = HOME_TESTIMONIALS.length;

type TestimonialsCarouselAreaProps = {
  slidesToShow: number;
  autoplayEnabled: boolean;
};

const TestimonialsCarouselArea: React.FC<TestimonialsCarouselAreaProps> = ({
  slidesToShow,
  autoplayEnabled,
}) => {
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const carouselState = useCarousel({ wrapAround: false });
  const { next, prev, idx, setIdx, resettingRef } = carouselState;
  const focusedIdx = Math.max(0, Math.min(idx, SLIDE_COUNT - 1));
  const maxIdx = Math.max(0, SLIDE_COUNT - slidesToShow);
  const canPrev = focusedIdx > 0;
  const canNext = focusedIdx < maxIdx;
  const idxRef = useRef(idx);
  idxRef.current = idx;

  const isSlideVisible = (index: number) => {
    for (let offset = 0; offset < slidesToShow; offset += 1) {
      if (focusedIdx + offset === index) return true;
    }
    return false;
  };

  // Keep index in range when slidesToShow changes (e.g. resize).
  useEffect(() => {
    if (idxRef.current > maxIdx) {
      resettingRef.current = true;
      setIdx(maxIdx);
    }
  }, [maxIdx, resettingRef, setIdx]);

  // When autoplay stops (off-screen / hidden tab), park on a clamped index
  // with an immediate spring snap so resume never catches up through many slides.
  useEffect(() => {
    if (autoplayEnabled) {
      return;
    }

    const current = idxRef.current;
    const parked = Math.max(0, Math.min(current, maxIdx));
    if (current !== parked) {
      resettingRef.current = true;
      setIdx(parked);
    }
  }, [autoplayEnabled, maxIdx, resettingRef, setIdx]);

  useEffect(() => {
    if (!autoplayEnabled || autoplayPaused) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const timer = window.setInterval(() => {
      setIdx((current) => {
        if (current >= maxIdx) {
          resettingRef.current = true;
          return 0;
        }
        return current + 1;
      });
    }, 5000);

    return () => window.clearInterval(timer);
  }, [autoplayEnabled, autoplayPaused, maxIdx, resettingRef, setIdx]);

  return (
    <CarouselWrap
      onMouseEnter={() => setAutoplayPaused(true)}
      onMouseLeave={() => setAutoplayPaused(false)}
      onFocus={() => setAutoplayPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setAutoplayPaused(false);
        }
      }}
    >
      <CarouselControls>
        <CarouselButton
          type="button"
          aria-label="Previous testimonial"
          disabled={!canPrev}
          onClick={() => {
            if (canPrev) prev();
          }}
        >
          <img
            src={arrowRight}
            alt=""
            style={{ transform: 'rotate(180deg)' }}
          />
        </CarouselButton>
        <CarouselButton
          type="button"
          aria-label="Next testimonial"
          disabled={!canNext}
          onClick={() => {
            if (canNext) next();
          }}
        >
          <img src={arrowRight} alt="" />
        </CarouselButton>
      </CarouselControls>
      <CarouselTrack>
        <CarouselOuter>
          <TestimonialCarousel
            slidesToShow={slidesToShow}
            offset={0}
            {...carouselState}
          >
            {HOME_TESTIMONIALS.map((testimonial, index) => (
              <CarouselSlide
                key={testimonial.author}
                aria-hidden={!isSlideVisible(index)}
              >
                <TestimonialCard>
                  <CardStars aria-hidden="true">
                    <StarIcons fullCount={5} variant="card" />
                  </CardStars>
                  <CardCopy>
                    <CardQuote>{testimonial.quote}</CardQuote>
                    <CardBody>{testimonial.body}</CardBody>
                  </CardCopy>
                  <CardAuthor>{testimonial.author}</CardAuthor>
                </TestimonialCard>
              </CarouselSlide>
            ))}
          </TestimonialCarousel>
        </CarouselOuter>
      </CarouselTrack>
    </CarouselWrap>
  );
};

export const HomeTestimonialsSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [mounted, setMounted] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const { isMobile, isTablet } = useIsomorphicBreakpoints();
  const mountainsParallaxY = useElementParallax(
    sectionRef,
    MOUNTAINS_PARALLAX_INTENSITY,
  );
  const slidesToShow = isMobile ? 1 : isTablet ? 2 : 3;

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onVisibility = () => {
      setPageVisible(document.visibilityState === 'visible');
    };
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: '0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted]);

  if (!mounted) {
    return null;
  }

  return (
    <TestimonialsSection
      ref={sectionRef}
      id="testimonials"
      $variant="testimonials"
      $dark
    >
      <MountainsBackground
        src={mountainsBg}
        alt=""
        aria-hidden="true"
        $parallaxY={mountainsParallaxY}
      />
      <SectionContainer>
        <Layout>
          <Intro>
            <IntroCopy>
              <EntranceHeadline as={HeadingH2} $inverse>
                <HomeMobileOnly>240,000 Iron Mountain customers</HomeMobileOnly>
                <HomeDesktopOnly>
                  240,000
                  <br />
                  Iron Mountain
                  <br />
                  customers
                </HomeDesktopOnly>
              </EntranceHeadline>
              <IntroBody>
                The company 95% of the Fortune 1000 trusts with their records.
              </IntroBody>
            </IntroCopy>
            <TrustBadge
              href="https://www.trustpilot.com/review/clutter.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={trustPilotLogo} alt="Trustpilot" />
              <LabelSm
                style={{
                  color: TOKENS.color.iconFillLight,
                  whiteSpace: 'nowrap',
                }}
              >
                4.0/5
              </LabelSm>
              <TrustStars aria-hidden="true">
                <StarIcons />
              </TrustStars>
            </TrustBadge>
          </Intro>

          <TestimonialsCarouselArea
            slidesToShow={slidesToShow}
            autoplayEnabled={inView && pageVisible}
          />
        </Layout>
      </SectionContainer>
    </TestimonialsSection>
  );
};
